/**
 * Локальный журнал «мои отправленные вопросы» (retention-answer-loop).
 *
 * Продуктовое решение: спрашивающий видит ответ СРАЗУ, как только автор
 * ответит — БЕЗ серверной идентичности (никакого account/session на стороне
 * анонимного спрашивающего). Устройство спрашивающего само помнит id своих
 * вопросов в localStorage; сервер лишь отдаёт вид ответа по `questionId`
 * (см. `security/threat-models/answer-view.md`).
 *
 * localStorage может быть недоступен (private mode, отключённое хранилище):
 * читаем/пишем в try/catch — деградируем к «список пуст» / «запись не
 * сохранилась», а не к падению (CANON G3, зеркалим `codeOnboarding.ts`).
 */

export const SENT_QUESTIONS_KEY = 'veylme.sentQuestions';

/** Потолок хранимых записей — FIFO, старые дропаются (не разрастаемся вечно). */
const MAX_ENTRIES = 50;

export interface SentQuestion {
  questionId: string;
  createdAt: string;
  /** Необязательная подпись (например, prompt ящика) — НЕ данные владельца ящика. */
  label?: string;
}

function isSentQuestion(value: unknown): value is SentQuestion {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.questionId !== 'string' || typeof v.createdAt !== 'string') {
    return false;
  }
  if ('label' in v && v.label !== undefined && typeof v.label !== 'string') {
    return false;
  }
  return true;
}

/** Читает список отправленных вопросов, самые новые — первыми. */
export function getSentQuestions(): SentQuestion[] {
  try {
    const raw = localStorage.getItem(SENT_QUESTIONS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSentQuestion);
  } catch {
    // localStorage недоступен или JSON битый — деградируем к пустому списку.
    return [];
  }
}

/**
 * Добавляет новый вопрос в начало списка (newest first). FIFO-потолок
 * `MAX_ENTRIES` — старые записи дропаются. Молча no-op при недоступном
 * localStorage.
 */
export function addSentQuestion(entry: SentQuestion): void {
  try {
    const current = getSentQuestions();
    const next = [entry, ...current].slice(0, MAX_ENTRIES);
    localStorage.setItem(SENT_QUESTIONS_KEY, JSON.stringify(next));
  } catch {
    // localStorage недоступен — запись не сохранится, «Мои вопросы» просто
    // не покажет этот вопрос. Допустимый деградированный путь (CANON G3).
  }
}
